import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FilePlus2, FolderPlus, X } from 'lucide-react';

const MODAL_CONFIG = {
    file: {
        title: 'Create File',
        description: 'Add a new file anywhere in this workspace using a relative path.',
        placeholder: 'components/NewPanel.jsx or README.md',
        cta: 'Create File',
        icon: FilePlus2,
        helper: 'Use forward slashes and type the location you want inside the workspace. You do not need to start with src/.',
        assistantNote: 'You can also ask your assistant to add files for you.',
    },
    folder: {
        title: 'Create Folder',
        description: 'Add a new folder anywhere in this workspace using a relative path.',
        placeholder: 'components/ui or docs',
        cta: 'Create Folder',
        icon: FolderPlus,
        helper: 'Use forward slashes and type the location you want inside the workspace. You do not need to start with src/.',
    },
};

const CreatePathModal = ({ isOpen, onClose, onConfirm, itemType = 'file', isSubmitting = false }) => {
    const [pathValue, setPathValue] = useState('');

    useEffect(() => {
        if (isOpen) {
            setPathValue('');
        }
    }, [isOpen, itemType]);

    if (!isOpen) return null;

    const config = MODAL_CONFIG[itemType] || MODAL_CONFIG.file;
    const Icon = config.icon;
    const normalizedPath = pathValue.trim();

    const handleSubmit = () => {
        if (!normalizedPath || isSubmitting) return;
        onConfirm(normalizedPath);
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 8 }}
                        className="relative w-full max-w-lg rounded-xl border border-border-color bg-surface shadow-xl"
                    >
                        <div className="flex items-center justify-between border-b border-border-color px-5 py-4">
                            <div className="flex items-center gap-2.5">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                    <Icon size={18} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-on-surface">{config.title}</h2>
                                    <p className="text-[12px] text-on-surface-dim">{config.description}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-full p-1 text-on-surface-dim transition-colors hover:bg-surface-dark hover:text-on-surface"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-3">
                            <div className="space-y-1.5">
                                <label htmlFor="workspace-path-input" className="text-[12px] font-medium uppercase tracking-[0.12em] text-on-surface-dim">
                                    Relative Path
                                </label>
                                <input
                                    id="workspace-path-input"
                                    type="text"
                                    value={pathValue}
                                    onChange={(event) => setPathValue(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            handleSubmit();
                                        }
                                    }}
                                    placeholder={config.placeholder}
                                    autoFocus
                                    className="w-full rounded-lg border border-border-color bg-background-dark px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-dim focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                />
                            </div>

                            <div className="rounded-lg border border-border-color bg-background-dark/50 px-3 py-2 text-[12px] text-on-surface-dim space-y-1">
                                <p>{config.helper}</p>
                                {config.assistantNote ? <p>{config.assistantNote}</p> : null}
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-border-color px-5 py-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-md bg-surface-dark px-4 py-2 text-sm font-semibold text-on-surface transition-colors hover:bg-surface-raised"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!normalizedPath || isSubmitting}
                                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-primary/50"
                            >
                                {isSubmitting ? 'Creating...' : config.cta}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default CreatePathModal;